import { useMemo, useState, useCallback } from 'react'
import { Canvas } from '@react-three/fiber'
import { ACESFilmicToneMapping, SRGBColorSpace } from 'three'
import { useGLTF } from '@react-three/drei'
import HoverLayer from './components/HoverLayer'
import Camera from './components/Camera'
import CameraMarkers from './components/CameraMarkers'
import World from './World'
import VolumetricBeam from './components/VolumetricBeam'
import AnimatedLightAndPortrait from './components/AnimatedLightAndPortrait'
import CameraFade from './components/CameraFade'
import GoBackButton from './components/GoBackButton'
import InteractableModal from './components/InteractableModal'
import AnimationMixer from './components/AnimationMixer'
import LoadingSplash from './components/LoadingSplash'
import { extractCameras, extractInteractables, extractProxies, INIT_ANIMATIONS } from './utils'
import { shouldShowGoBack, useCameraSelection } from './components/CameraMarkers'

const MODEL_URL = '/gltf/room.gltf'
const READY_REVEAL_DELAY_MS = 500

function CanvasContent({ activeCam, onSelectCam, onReady, isReady, onOpenModal, modalUrl }: { activeCam: number; onSelectCam: (i: number) => void; onReady: () => void; isReady: boolean; onOpenModal: (url: string) => void; modalUrl: string | null }) {
  const { scene, animations } = useGLTF(MODEL_URL)
  const [isCurrentlyPlayingAnimations, setIsCurrentlyPlayingAnimations] = useState(true)
  const [activeAnimations, setActiveAnimations] = useState({ animationsList: INIT_ANIMATIONS, callback: () => {} })
  const cameras = useMemo(() => extractCameras(scene), [scene])
  const proxies = useMemo(() => extractProxies(scene), [scene])
  const interactables = useMemo(() => extractInteractables(scene), [scene])

  return (
    <>
      <Camera cameras={cameras} activeCam={activeCam} dragEnabled={!isCurrentlyPlayingAnimations} />
      <World scene={scene} onReady={onReady} />
      {/* Mounted before `isReady` so the postprocessing composer (Bloom, N8AO,
          Outline, SMAA…) compiles its EffectPass shaders behind the black load
          overlay instead of stuttering on the first visible frame. Pointer events
          hit the overlay until ready, so the early interactables stay inert. */}
      <HoverLayer interactables={interactables} activeCam={activeCam} isCurrentlyPlayingAnimations={isCurrentlyPlayingAnimations} onOpenModal={onOpenModal} setActiveAnimations={setActiveAnimations} />
      {/* Mounted before `isReady` (like HoverLayer) so their GPU programs are folded
          into useSceneProcessing's single compileAsync instead of compiling on the
          first visible frame. The point light especially: adding a light after the
          scene compiles changes NUM_POINT_LIGHTS and forces a full material recompile,
          so it must be present during the compile. Both stay inert while hidden — the
          beam rides the (invisible) scene, the light's intensity is 0 off its cams. */}
      <VolumetricBeam scene={scene} activeCam={activeCam} />
      <AnimatedLightAndPortrait scene={scene} activeCam={activeCam} />
      {isReady && (
        <>
          {/* Gated until ready: the mixer must not play the intro behind the overlay,
              the fade's intro-from-black must coincide with the reveal, and the markers
              are DOM (no program to precompile) that would otherwise float over the
              loading screen. */}
          <AnimationMixer scene={scene} animations={animations} activeAnimations={activeAnimations} setIsCurrentlyPlayingAnimations={setIsCurrentlyPlayingAnimations} />
          <CameraMarkers proxies={proxies} activeCam={activeCam} onSelectCam={onSelectCam} isVisible={!isCurrentlyPlayingAnimations && !modalUrl} />
          <CameraFade activeCam={activeCam} />
        </>
      )}
    </>
  )
}

export default function App() {
  const { activeCam, onSelectCam, modalUrl, onOpenModal, onCloseModal } = useCameraSelection()
  const [ready, setReady] = useState(false)
  // Hold on the full (100%) progress bar for a beat once processing finishes,
  // before fading the splash and kicking off the intro reveal/animations.
  const onReady = useCallback(() => { setTimeout(() => setReady(true), READY_REVEAL_DELAY_MS) }, [])

  return (
    <>
      <Canvas
        shadows
        frameloop={modalUrl ? 'never' : 'always'}
        style={{ width: '100vw', height: '100vh' }}
        gl={{ outputColorSpace: SRGBColorSpace, toneMapping: ACESFilmicToneMapping, toneMappingExposure: 1.0 }}
      >
        <CanvasContent activeCam={activeCam} onSelectCam={onSelectCam} isReady={ready} onReady={onReady} onOpenModal={onOpenModal} modalUrl={modalUrl} />
      </Canvas>
      <LoadingSplash ready={ready} />
      {shouldShowGoBack(activeCam) && <GoBackButton onSelectCam={onSelectCam} />}
      {modalUrl && <InteractableModal url={modalUrl} onClose={onCloseModal} />}
    </>
  )
}
